import { Link } from '@mui/material'
import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation, Navigate, Outlet, Link as RouterLink } from 'react-router-dom'
import origin from '../../utils/origin'
import redirectWithReturn from '../../utils/redirectWithReturn'
import { useMe } from '../api/users'
import useEventListener from '../useEventListener'

export const UserAuthContext = React.createContext({
  user: null,
  isAuthorized: false,
  login: (credentials, callback) => {},
  logout: (callback) => {},
  error: null,
})

export function UserAuthProvider({ children }) {
  const navigate = useNavigate()
  const location = useLocation()

  const { firstRun, user, error, fetchUser } = useMe()

  useEffect(() => {
    if (user && user.from) {
      const state = { ...(location?.state ?? {}) }
      delete state.from
      navigate(user.from || '/', { replace: true, state })
      delete user.from
    }
    if (!firstRun && user === null) {
      navigate('/', { state: {} })
    }
    // eslint-disable-next-line
  }, [user])

  let login = () => redirectWithReturn('/login', location)

  let logout = () => redirectWithReturn('/logout', location)

  let isAuthorized = Boolean(user?.id) && user?.isActive
  let value = { user, isAuthorized, login, logout, error, fetchUser }

  return (
    <UserAuthContext.Provider value={value}>{firstRun ? null : children}</UserAuthContext.Provider>
  )
}

export function useAuth() {
  return React.useContext(UserAuthContext)
}

const IconButton = React.lazy(() => import('@mui/material/IconButton'))
const ListItemIcon = React.lazy(() => import('@mui/material/ListItemIcon'))
const ListItemText = React.lazy(() => import('@mui/material/ListItemText'))
const Menu = React.lazy(() => import('@mui/material/Menu'))
const MenuItem = React.lazy(() => import('@mui/material/MenuItem'))
const Button = React.lazy(() => import('@mui/material/Button'))

const LogoutIcon = React.lazy(() => import('@mui/icons-material/Logout'))

const ProfileMenuItem = React.lazy(() => import('../../components/_ui/ProfileItemMenu'))
const UserAvatar = React.lazy(() => import('../../components/_ui/UserAvatar'))

const ErrorPage = React.lazy(() => import('../../components/errors/ErrorPage'))

function AuthIFrame() {
  const auth = useAuth()
  const { enabled, setEnabled, setFirstRun } = useMe()
  const iframeRef = useRef(null)

  const refreshAuth = (event) => {
    const { protocol, host } = window.location

    const windowOrigin = `${protocol}//${host}`

    if (event.origin !== windowOrigin && event.origin !== origin) {
      return
    }

    if (event.data?.action === 'refresh_auth') {
      setEnabled(true)
      setFirstRun(false)
      if ((!event.data.error && !auth.user) || (event.data.error && auth.user)) {
        auth.fetchUser()
      }
    }
  }

  useEffect(() => {
    if (enabled && !auth.user) {
      auth.fetchUser()
    }
  }, [enabled, auth.user])

  useEffect(() => {
    if (iframeRef?.current) {
      iframeRef.current.src = `${origin}/auth`
    }
  }, [auth])

  useEventListener('message', refreshAuth)

  return auth.user ? null : (
    <iframe
      ref={iframeRef}
      src={`${origin}/auth`}
      sandbox='allow-forms allow-scripts allow-same-origin'
      title='auth'
      style={{
        display: 'absolute',
        top: -1,
        left: -1,
        width: 0,
        height: 0,
        margin: 0,
        padding: 0,
        border: 'none',
      }}
    ></iframe>
  )
}

export function UserAuthStatus() {
  const auth = useAuth()
  const location = useLocation()
  const [accountAnchorEl, setAccountAnchorEl] = useState(null)

  const handleAccountMenu = (event) => {
    setAccountAnchorEl(event.currentTarget)
  }

  const handleAccountMenuClose = () => {
    setAccountAnchorEl(null)
  }

  const handleLogout = () => {
    handleAccountMenuClose()
    auth.logout()
  }

  const handleProfile = () => redirectWithReturn('/profile', location)

  if (!auth.user) {
    return (
      <div>
        {/* <Button component={RouterLink} to='/enquiry' color='primary'>
          Enquiry
        </Button> */}
        <Button href={`${origin}/login`} color='primary'>
          Log in
        </Button>
      </div>
    )
  }

  return (
    <div>
      {/* {!auth.isAuthorized && (
        <Button component={RouterLink} to='/enquiry' color='primary'>
          Enquiry
        </Button>
      )} */}
      <IconButton
        aria-label='account'
        aria-controls='menu-account'
        aria-haspopup='true'
        aria-expanded={Boolean(accountAnchorEl)}
        onClick={handleAccountMenu}
        color='inherit'
      >
        <UserAvatar size={32} user={auth.user} />
      </IconButton>
      <Menu
        id='menu-account'
        anchorEl={accountAnchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(accountAnchorEl)}
        onClose={handleAccountMenuClose}
      >
        <ProfileMenuItem onClick={handleProfile} />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  )
}

export function RequireAuth() {
  let auth = useAuth()
  let location = useLocation()
  let navigate = useNavigate()

  useEffect(() => {
    if (!auth.user) {
      auth.login()
    }
    // eslint-disable-next-line
  }, [auth.user])

  if (!auth.isAuthorized) {
    return (
      <ErrorPage error={{ statusText: 'Access Denied' }}>
        <p>You don't have access to this site.</p>
        <p>
          If you wish to access this site, please contact our IT team:{' '}
          <Link component='a' href='mailto:technology@htgroup.com.my'>
            technology@htgroup.com.my
          </Link>{' '}
        </p>
      </ErrorPage>
    )
  }
  return <Outlet />
}

export function RequireRole(roles) {
  let auth = useAuth()

  if (!auth.isAuthorized || !roles.includes(auth.user?.role)) {
    return (
      <ErrorPage error={{ statusText: 'Access Denied' }}>
        <p>You don't have the necessary privileges to access this page.</p>
        <p>
          If you believe that this is a mistake, please contact our IT team:{' '}
          <Link component='a' href='mailto:technology@htgroup.com.my'>
            technology@htgroup.com.my
          </Link>{' '}
        </p>
      </ErrorPage>
    )
  }

  return <Outlet />
}

export function RequireAdmin() {
  let auth = useAuth()

  if (!auth.isAuthorized || !auth.user?.isAdmin) {
    return (
      <ErrorPage error={{ statusText: 'Access Denied' }}>
        <p>You don't have the necessary privileges to access this page.</p>
        <p>
          If you believe that this is a mistake, please contact our IT team:{' '}
          <Link component='a' href='mailto:technology@htgroup.com.my'>
            technology@htgroup.com.my
          </Link>{' '}
        </p>
      </ErrorPage>
    )
  }

  return <Outlet />
}
